import { formatValueBase } from "../../../utils/helpers";
import ProgramPie from "../charts/ProgramPie";
import { blendRgbOverBackground, getContrastTextFromRgb, hexToRgb } from "../utils/helpers";
import { SKPCFPartners } from "../utils/partners";

interface ProgramDetailStatsProps {
  program: SKPCFPartners;
}

export default function ProgramDetailStats({ program }: ProgramDetailStatsProps) {
    const color = program?.color ?? "#C2410C";
    // calculate text color for toggling black/white text
    const blendedBg = blendRgbOverBackground(color, 0.15, "#FFFFFF");
    const textColor = getContrastTextFromRgb(blendedBg);

    const funding = program?.funding?.value ?? 0;
    const dates = program?.funding?.dates ?? "2022 - 2025";
    const participants = program?.funding?.participants ?? 0;
    const badgeImg = program?.src;
    const demoData = program?.participantData?.demographics;
    const genderData = program?.participantData?.gender;

    const female = genderData?.find((r) => r.label === "Female")?.value ?? 0;
    const male = genderData?.find((r) => r.label === "Male")?.value ?? 0;
    const highlights = program?.participantData?.highlights ?? [];

    if (!funding || !dates || !participants || !demoData) return null;

    return (
        <section
        className="mb-8 rounded-2xl shadow-xl"
        style={{ backgroundColor: `rgba(${hexToRgb(color)}, 0.1)` }}
        >
            <div className="px-4 py-4 sm:px-6 md:px-8">
                {/* Top funding section */}
                <div className="flex flex-col gap-4 xs:flex-row xs:items-center xs:gap-6 md:gap-10">
                <div
                    className="h-14 w-14 shrink-0 overflow-hidden rounded-full bg-white sm:h-16 sm:w-16"
                    style={{ boxShadow: `0 0 0 4px ${color}` }}
                >
                    {badgeImg ? (
                    <img src={badgeImg} alt="" className="h-full w-full object-cover p-1" />
                    ) : null}
                </div>

                <div className="min-w-0">
                    <h2 className="text-lg font-semibold text-gray-900 sm:text-xl md:text-2xl">
                    SKPCF Funded
                    </h2>
                    <h3
                    className="break-words text-3xl font-extrabold leading-tight sm:text-5xl md:text-6xl lg:text-7xl"
                    style={{ color }}
                    >
                    {formatValueBase(funding, "currency")}
                    </h3>
                    <h2 className="text-base font-semibold text-gray-900 sm:text-xl md:text-2xl">
                    from {dates}
                    </h2>
                </div>
                </div>

                {/* Participant section */}
                <div className="mt-4 grid grid-cols-1 md:mt-8 md:grid-cols-[auto_1fr_auto] md:items-center">
                    <div className="mx-auto md:mx-0 flex items-center gap-4">
                        <svg
                        className="h-12 w-12 sm:h-20 sm:w-20 md:h-24 md:w-24"
                        viewBox="0 0 148 93"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                        >
                        <path d="M70.5017 61.0402C79.859 60.0857 96.1117 64.4602 103.258 71.1152C107.06 74.6561 105.809 87.2671 105.748 92.675L77.9012 92.6727C66.0534 92.6728 54.0551 92.5729 42.2194 92.7014C42.74 87.4896 40.8826 75.2364 44.1043 71.7378C49.2949 66.1012 63.283 61.7295 70.5017 61.0402Z" fill={color} />
                        <path d="M29.0772 36.2606C33.4848 35.8754 47.8355 38.1066 51.0902 41.0698C51.6617 43.6481 51.4878 46.3093 52.5772 48.7547C53.764 51.4183 55.1993 53.677 56.8842 56.039C56.7374 57.6642 51.2811 59.3599 49.7318 60.0858C46.8289 61.4459 39.9773 65.1045 38.3044 67.7906L38.2182 67.9316C35.3009 68.3265 31.2405 68.1632 28.1876 68.1319L12.5716 68.1389C8.30235 68.1518 4.35189 68.2376 0.0956017 67.9536C0.0345415 63.6952 -0.216552 53.9657 0.46525 50.294C1.97179 42.1811 22.4532 37.0329 29.0772 36.2606Z" fill={color} />
                        <path d="M113.592 36.2586C114.034 36.2216 114.477 36.1977 114.92 36.1868C122.12 36.0219 144.053 40.7598 147.227 49.059C148.424 52.1879 147.857 63.8624 147.918 67.9493C145.871 68.2213 142.087 68.0956 139.905 68.0928L125.62 68.0763C123.161 68.0667 120.702 68.0758 118.243 68.1035C115.518 68.1437 112.162 68.4388 109.607 67.727C109.194 67.1331 108.427 66.5149 107.905 65.9865C104.507 62.5497 100.292 60.7856 95.9836 58.8074C94.6176 58.1803 91.9638 57.3402 91.041 56.2544L91.101 55.9644C92.1872 55.2221 92.5707 54.0353 93.2912 52.991C95.9665 49.1135 96.6626 45.4246 96.7034 40.757C98.0216 40.4937 100.507 39.2 102.033 38.7512C105.907 37.6125 109.603 36.7754 113.592 36.2586Z" fill={color} />
                        <path d="M29.1766 0.505728C37.8552 -0.891041 46.0092 5.05262 47.3353 13.7423C48.6616 22.4319 42.6518 30.5372 33.9516 31.7927C25.3508 33.0338 17.3584 27.1076 16.0473 18.5174C14.7363 9.92703 20.5973 1.88664 29.1766 0.505728Z" fill={color} />
                        <path d="M113.739 0.505733C122.417 -0.891036 130.571 5.05246 131.897 13.7421C133.224 22.4317 127.214 30.5369 118.514 31.7924C109.913 33.0336 101.921 27.1074 100.61 18.5172C99.2985 9.92687 105.16 1.88648 113.739 0.505733Z" fill={color} />
                        <path d="M72.0064 25.1415C80.6891 24.0384 88.6166 30.1977 89.6935 38.8839C90.7705 47.5699 84.5872 55.4788 75.8979 56.5296C67.2456 57.5757 59.3778 51.4246 58.3054 42.7754C57.233 34.1263 63.3605 26.24 72.0064 25.1415Z" fill={color} />
                        </svg>
                        <p
                        className="text-4xl font-extrabold leading-tight sm:text-5xl md:text-6xl lg:text-7xl"
                        style={{ color }}
                        >
                        {participants}
                        </p>
                    </div>

                    <div className="text-center md:text-left">
                        <p className="text-lg font-semibold text-gray-900 sm:text-xl md:text-2xl">
                        Participants Served
                        </p>
                    </div>

                    <div className="mt-4 border-t border-black/15 pt-4">
                        <p className="mb-3 text-center text-xs font-semibold uppercase tracking-wide text-gray-600 md:text-left">
                            Gender breakdown
                        </p>

                        <div className="grid grid-cols-2 gap-3">
                            <div 
                                className="rounded-xl px-4 py-3 text-center shadow-sm"
                                style={{ backgroundColor: `rgba(${hexToRgb(color)}, 0.15)` }}
                            >
                                <p className="text-2xl font-extrabold sm:text-3xl" style={{ color: textColor }}>
                                    {female}
                                </p>
                                <p className="text-sm font-semibold sm:text-base" style={{ color: textColor }}>
                                    Female
                                </p>
                            </div>

                            <div 
                                className="rounded-xl px-4 py-3 text-center shadow-sm"
                                style={{ backgroundColor: `rgba(${hexToRgb(color)}, 0.15)` }}
                            >
                                <p className="text-2xl font-extrabold sm:text-3xl" style={{ color: textColor }}>
                                    {male}
                                </p>
                                <p className="text-sm font-semibold sm:text-base" style={{ color: textColor }}>
                                    Male
                                </p>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="mt-4 md:mt-6 border-t border-black/15 pt-4">
                    <p className="mb-3 text-center text-xs font-semibold uppercase tracking-wide text-gray-600 md:text-left">
                        Demographic breakdown
                    </p>

                    <ProgramPie data={demoData} height={220} />
                </div>

                {highlights.length > 0 && (
                    <div className="mt-6 border-t border-black/15 pt-4">
                        <p className="mb-3 text-center text-xs font-semibold uppercase tracking-wide text-gray-600 md:text-left">
                            Additional outcomes
                        </p>

                        <div className="grid gap-3">
                        {highlights.map((item, index) => (
                            <div
                                key={`${item.value}-${index}`}
                                className="rounded-2xl px-4 py-4 shadow-sm"
                                style={{ backgroundColor: `rgba(${hexToRgb(color)}, 0.15)` }}
                            >
                            <div className="flex items-start gap-2">
                                <div
                                    className="min-w-[72px] text-3xl font-extrabold leading-none sm:text-4xl"
                                    style={{ color: textColor }}
                                >
                                {item.value}
                                </div>

                                <div className="min-w-0">
                                <p className="text-sm font-semibold sm:text-base" style={{ color: textColor }}>
                                    {item.label}
                                </p>

                                {item.detail && (
                                    <p className="mt-1 text-sm opacity-80" style={{ color: textColor }}>
                                    {item.detail}
                                    </p>
                                )}
                                </div>
                            </div>
                            </div>
                        ))}
                        </div>
                    </div>
                )}
            </div>
        </section>
    );
}
